import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, Loader2Icon } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { Reveal } from '../components/Reveal';
import { images, socials } from '../data/site';
const intents = ['Work together', 'Speaking enquiry', 'Investment', 'Media & press'];
type Status = 'idle' | 'sending' | 'sent' | 'error';
export function Contact() {
  const [intent, setIntent] = useState(intents[0]);
  const [form, setForm] = useState({
    name: '',
    email: '',
    org: '',
    message: ''
  });
  const [status, setStatus] = useState<Status>('idle');
  const update = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm(f => ({
    ...f,
    [key]: e.target.value
  }));
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus('error');
      return;
    }
    setStatus('sending');
    window.setTimeout(() => setStatus('sent'), 1100);
  };
  const field = 'w-full border-b border-charcoal/20 bg-transparent pb-3 pt-2 text-[1rem] text-charcoal placeholder:text-charcoal/30 transition-colors duration-300 focus:border-charcoal focus:outline-none';
  return <>
      <PageHeader eyebrow="Contact" title="Let’s build something meaningful." intro="Tell me what you are building, backing or planning. I read every note personally." data-id="element-259" data-mp="1j7d6holr56tu" />

      <section className="px-6 pb-28 sm:px-10 sm:pb-40" data-id="element-260" data-mp="qhmqtb4h1wvm">
        <div className="mx-auto grid max-w-[92rem] gap-16 lg:grid-cols-12 lg:gap-20" data-id="element-261" data-mp="qk8xz9ngaadb">
          <div className="lg:col-span-4" data-id="element-262" data-mp="vfxypgb7hxb7">
            <Reveal data-id="element-263" data-mp="10g2k9kqz1i8a">
              <img src={images.headshot} alt="Amoye Henry" className="mb-12 h-32 w-32 rounded-full object-cover" data-id="element-264" data-mp="10glyaqdi3mh1" />
              <dl className="space-y-10" data-id="element-265" data-mp="2bv918nrrgonz">
                <div className="border-t border-charcoal/15 pt-5" data-id="element-266" data-mp="2btxwakmkxava">
                  <dt className="eyebrow text-charcoal/40" data-id="element-267" data-mp="1m6kdx2dos09w">Direct</dt>
                  <dd className="mt-3" data-id="element-268" data-mp="1h8aicqo1uxnr">
                    <a href="mailto:hello@amoyehenry.com" className="link-underline font-serif text-2xl font-light text-charcoal" data-id="element-269" data-mp="1h78utmpukip5">
                      hello@amoyehenry.com
                    </a>
                  </dd>
                </div>
                <div className="border-t border-charcoal/15 pt-5" data-id="element-270" data-mp="13hniuwagt3qm">
                  <dt className="eyebrow text-charcoal/40" data-id="element-271" data-mp="yi124cvfh5d2">Based</dt>
                  <dd className="mt-3 text-sm leading-relaxed text-charcoal/70" data-id="element-272" data-mp="yfohfryacgwb">
                    London · Lagos
                    <br data-id="element-273" data-mp="18fytvachxkaf" />
                    Working across 30+ countries
                  </dd>
                </div>
                <div className="border-t border-charcoal/15 pt-5" data-id="element-274" data-mp="kpun4u40h4f7">
                  <dt className="eyebrow text-charcoal/40" data-id="element-275" data-mp="ko1itlgvewad">Elsewhere</dt>
                  <dd className="mt-4 flex gap-6" data-id="element-276" data-mp="fqrsm3lnqglh">
                    {socials.map(s => <a key={s.label} href={s.href} target="_blank" rel="noreferrer" className="link-underline text-xs uppercase tracking-[0.16em] text-charcoal/60 hover:text-charcoal" data-id="element-277" data-mp="2rd25ohqwcrwq">
                        {s.label}
                      </a>)}
                  </dd>
                </div>
              </dl>
              <p className="mt-14 font-script text-3xl text-mocha" data-id="element-278" data-mp="3f366khcpadk1">Amoye</p>
            </Reveal>
          </div>

          <div className="lg:col-span-7 lg:col-start-6" data-id="element-279" data-mp="313objk8ta428">
            {status === 'sent' ? <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.7,
            ease: [0.22, 1, 0.36, 1]
          }} className="rounded-[1.75rem] border border-charcoal/[0.09] bg-ivorydeep/60 p-12 text-center" data-id="element-280" data-mp="35502bt3f8vc0">
                <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-charcoal text-ivory" data-id="element-281" data-mp="29zbz0443uhfq">
                  <CheckIcon className="h-5 w-5" strokeWidth={1.4} data-id="element-282" data-mp="2087k14efiwkw" />
                </span>
                <h2 className="mt-8 font-serif text-[2rem] font-light leading-tight text-charcoal" data-id="element-283" data-mp="205v4sz0230bj">
                  Thank you, {form.name.split(' ')[0]}.
                </h2>
                <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-charcoal/60" data-id="element-284" data-mp="2sw3r931mk6vx">
                  Your note has arrived. I reply to most enquiries within three
                  working days.
                </p>
              </motion.div> : <Reveal data-id="element-285" data-mp="2ew2f2rfwtd15">
                <form onSubmit={onSubmit} noValidate data-id="element-286" data-mp="2ewkm8j441bnu">
                  <fieldset data-id="element-287" data-mp="17k20gkz43dmn">
                    <legend className="eyebrow text-charcoal/40" data-id="element-288" data-mp="17kbpa58f7eqr">
                      I’m reaching out about
                    </legend>
                    <div className="mt-5 flex flex-wrap gap-2" data-id="element-289" data-mp="13kbcep0yzblt">
                      {intents.map(i => <button key={i} type="button" aria-pressed={intent === i} onClick={() => setIntent(i)} className={`rounded-full px-4 py-2 text-[0.75rem] uppercase tracking-[0.12em] transition-all duration-500 ${intent === i ? 'bg-charcoal text-ivory' : 'border border-charcoal/15 text-charcoal/55 hover:border-charcoal/40 hover:text-charcoal'}`} data-id="element-290" data-mp="3j6vt496j418r">
                          {i}
                        </button>)}
                    </div>
                  </fieldset>

                  <div className="mt-14 grid gap-10 sm:grid-cols-2" data-id="element-291" data-mp="2ck9y5liem3m1">
                    <div data-id="element-292" data-mp="2hil7lv4sec3q">
                      <label htmlFor="name" className="eyebrow text-charcoal/40" data-id="element-293" data-mp="2hhs9fijw8ux7">
                        Name
                      </label>
                      <input id="name" value={form.name} onChange={update('name')} placeholder="Your full name" className={`${field} mt-3`} data-id="element-294" data-mp="3ftmgu0lgqwmk" />
                    </div>
                    <div data-id="element-295" data-mp="7le22bhm9epc">
                      <label htmlFor="email" className="eyebrow text-charcoal/40" data-id="element-296" data-mp="7kmjc194bbgh">
                        Email
                      </label>
                      <input id="email" type="email" value={form.email} onChange={update('email')} placeholder="your@email.com" className={`${field} mt-3`} data-id="element-297" data-mp="2l03rl5thj4i" />
                    </div>
                  </div>

                  <div className="mt-10" data-id="element-298" data-mp="lf5g9x6almre">
                    <label htmlFor="org" className="eyebrow text-charcoal/40" data-id="element-299" data-mp="14zdsguhlg4nm">
                      Company or organisation
                    </label>
                    <input id="org" value={form.org} onChange={update('org')} placeholder="Optional" className={`${field} mt-3`} data-id="element-300" data-mp="19xflo88qhkct" />
                  </div>

                  <div className="mt-10" data-id="element-301" data-mp="m7uqmqzb2wn6">
                    <label htmlFor="message" className="eyebrow text-charcoal/40" data-id="element-302" data-mp="qzcpn9hrqn5i">
                      Your note
                    </label>
                    <textarea id="message" rows={5} value={form.message} onChange={update('message')} placeholder="What are you building, and where are you stuck?" className={`${field} mt-3 resize-none`} data-id="element-303" data-mp="cbhvdoo44aqb" />
                  </div>

                  {status === 'error' && <p role="alert" className="mt-6 text-sm text-mocha" data-id="element-304" data-mp="nq3dmyl8asfb">
                      Please add your name, email and a short note so I can reply
                      properly.
                    </p>}

                  <button type="submit" disabled={status === 'sending'} className="mt-12 inline-flex items-center gap-3 rounded-full bg-charcoal px-8 py-4 text-[0.8125rem] font-medium uppercase tracking-[0.1em] text-ivory transition-colors duration-500 hover:bg-mocha disabled:opacity-60" data-id="element-305" data-mp="8tm2a7d1hdsr">
                    {status === 'sending' && <Loader2Icon className="h-4 w-4 animate-spin" strokeWidth={1.6} data-id="element-306" data-mp="3m90iiyud71nl" />}
                    {status === 'sending' ? 'Sending' : 'Send message'}
                  </button>
                </form>
              </Reveal>}
          </div>
        </div>
      </section>
    </>;
}